import { useCallback } from 'react';

import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { CUSTOMER_TYPE } from '@savgroup-front-common/types';
import { OwnerService } from '@/api';

import { CreateClaimContext, CreateClaimValues } from '../../../types';

import prepareAuthenticateWithProductReferencePayload from './adapters/prepareAuthenticateWithProductReferencePayload';

interface UseNewFormContentReturnValues {
  handleSubmit: ({
    values,
    context,
  }: {
    values: CreateClaimValues;
    context: CreateClaimContext;
  }) => Promise<void>;
}

export const useCreateClaimContent = (): UseNewFormContentReturnValues => {
  const { pushErrors } = useToasts();

  const handleSubmit = useCallback(
    async ({
      values,
      context,
    }: {
      values: CreateClaimValues;
      context: CreateClaimContext;
    }): Promise<void> => {
      const { hasInvoice, hasPartner } = context;
      const {
        sellerId,
        tenantId,
        genericId,
        brandId,
        owner: { email, ownerAddress },
      } = values;

      if (hasInvoice) {
        const response = await OwnerService.createAnonymousOrder({
          owner: values.owner,
          sellerId,
          tenantId,
          orderReference: values.orderReference as string,
          purchaseDate: values.purchaseDate,
        });

        if (response.failure) {
          pushErrors(response.errors);

          throw response.errors;
        }

        window.location = response.value as unknown as string & Location;

        return undefined;
      }

      if (hasPartner && values.productReference) {
        const payload = prepareAuthenticateWithProductReferencePayload({
          tenantId,
          sellerId: values?.productReference?.seller?.id || tenantId,
          customerType:
            values?.productReference?.seller?.customerType ||
            CUSTOMER_TYPE.NOT_SET,
          data: {
            customerInformation: values.owner,
            productReference: {
              model: values.productReference.model,
              seller: values.productReference.seller,
              salesChannel: values.productReference.salesChannel,
              purchaseDate: values.productReference.purchaseDate,
              productPrice: values.productReference.productPrice,
            },
          },
        });

        const response = await OwnerService.createOrderAndShareLink(payload);

        if (response.failure) {
          pushErrors(response.errors);

          throw response.errors;
        }

        window.location = response.value as unknown as string & Location;

        return undefined;
      }

      const response = await OwnerService.setCreateOrderGenericProduct({
        sellerId,
        modelTypeId: genericId,
        brandId,
        purchaseDate: new Date(2000, 0, 1),
        ownerEmail: email,
        ownerAddress,
      });

      if (response.failure) {
        pushErrors(response.errors);

        throw response.errors;
      }

      window.location.href = response.value;

      return undefined;
    },
    [pushErrors],
  );

  return {
    handleSubmit,
  };
};
